import { Tooltip, VStack } from "@chakra-ui/react";
import type { ExpenseItem } from "@src/__generated__/graphql";
import { useExpenseItemsStore } from "@src/components/modules/expenses/listing/expense-items";
import type { ExpenseItemsTableItem } from "@src/components/modules/expenses/listing/expense-items/store";
import {
  getColorByStatus,
  getStatusMessage,
} from "@src/components/modules/expenses/listing/table/TableExpenses";
import { TIME_BILLING_URL_PARAMS } from "@src/components/modules/projects/time-billing/constants";
import { Link } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";

type EuroCurrencyStatusColumnProps = {
  invoiceStatus: ExpenseItem["expense"]["invoice_status"];
};

type CellComponentProps = {
  row: { original: ExpenseItemsTableItem };
};

export const ResponsibleColumn = ({ row }: CellComponentProps) => {
  const store = useExpenseItemsStore();
  if (!row.original.mandatory_id) return null;
  return store.usersMap.get(row.original.mandatory_id);
};

export const CostCenterColumn = ({ row }: CellComponentProps) => {
  const store = useExpenseItemsStore();
  if (!row.original.accounting_centre_id) return null;
  return store.accountingCentresMap.get(row.original.accounting_centre_id);
};

export const EuroCurrencyStatusColumn = ({
  invoiceStatus,
}: EuroCurrencyStatusColumnProps) => (
  <Tooltip
    hasArrow
    label={getStatusMessage(invoiceStatus)}
    placement="top"
    shouldWrapChildren
  >
    <Icon name="currency-euro-circle" color={getColorByStatus(invoiceStatus)} />
  </Tooltip>
);

type LockedTimeBillingColumnProps = {
  timeBillings: Array<ExpenseItemsTableItem["timeBillings"][0]>;
};

export const LockedTimeBillingColumn = ({
  timeBillings,
}: LockedTimeBillingColumnProps) => {
  const locked = timeBillings.find((tb) => tb.is_locked);

  if (!locked) return null;

  return (
    <Link
      target="_blank"
      href={{
        pathname: "/projects/time-billing",
        query: {
          id: locked.project.id,
          [TIME_BILLING_URL_PARAMS.EXPAND]: locked.id,
        },
      }}
    >
      <Icon name="lock-01" color="orange.500" /> {locked?.title ?? locked.id}
    </Link>
  );
};

type TimeBillingsColumnProps = {
  timeBillings: Array<ExpenseItemsTableItem["timeBillings"][0]>;
  sourceId: string;
};

export const TimeBillingsColumn = ({
  timeBillings,
  sourceId,
}: TimeBillingsColumnProps) => {
  if (!timeBillings?.length) return null;

  return (
    <VStack align="start" spacing="1">
      {timeBillings.map((timeBilling) => (
        <Link
          key={`${timeBilling.id}-${sourceId}`}
          target="_blank"
          href={{
            pathname: "/projects/time-billing",
            query: {
              id: timeBilling.project.id,
              [TIME_BILLING_URL_PARAMS.EXPAND]: timeBilling.id,
            },
          }}
        >
          {timeBilling.title ? timeBilling.title.trim() : timeBilling.id}
        </Link>
      ))}
    </VStack>
  );
};

type BrandColumnProps = {
  brandId: ExpenseItemsTableItem["brand_id"];
  color?: string;
};
export const BrandColumn = ({ brandId, color }: BrandColumnProps) => {
  const store = useExpenseItemsStore();

  if (!brandId) return null;

  const brand = store.brandsMap.get(brandId);

  if (!brand) return null;

  return (
    <Link
      href={{
        pathname: "/brands/detail",
        query: { id: brandId },
      }}
      target="_blank"
      color={color}
    >
      {brand}
    </Link>
  );
};

type ProjectColumnProps = {
  project: ExpenseItemsTableItem["expense"]["projects"][0];
  color?: string;
};
export const ProjectColumn = ({ project, color }: ProjectColumnProps) => {
  return (
    <Link
      href={{ pathname: "/projects/detail", query: { id: project.id } }}
      color={color}
      target="_blank"
    >
      {project.code} {project.title}
    </Link>
  );
};
